import { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import '../styles/index.css';

export default function UsageHistory() {
  const { user, formatCredits, creditsToUSD } = useApp();
  const [usageHistory, setUsageHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [operationFilter, setOperationFilter] = useState('all');

  useEffect(() => {
    const fetchUsageHistory = async () => {
      if (!user) return;

      try {
        const response = await fetch(`http://localhost:3001/api/billing/usage-history?userId=${user.uid}`);
        const data = await response.json();
        setUsageHistory(data.usageHistory || []);
      } catch (error) {
        console.error('Error fetching usage history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsageHistory();
  }, [user]);

  // Filter usage history
  const filteredHistory = usageHistory.filter(item => {
    // Date filter
    if (dateFilter !== 'all') {
      const itemDate = new Date(item.timestamp);
      const now = new Date();
      const diffDays = Math.floor((now - itemDate) / (1000 * 60 * 60 * 24));

      if (dateFilter === 'today' && diffDays > 0) return false;
      if (dateFilter === 'week' && diffDays > 7) return false;
      if (dateFilter === 'month' && diffDays > 30) return false;
    }

    // Operation filter
    if (operationFilter !== 'all' && item.operation !== operationFilter) return false;

    return true;
  });

  // Calculate summary statistics
  const summary = filteredHistory.reduce((acc, item) => {
    acc.totalCredits += item.creditsUsed || 0;
    acc.totalOperations += 1;
    return acc;
  }, { totalCredits: 0, totalOperations: 0 });

  // Get unique operation types
  const operationTypes = [...new Set(usageHistory.map(item => item.operation))];

  // Format operation name for display
  const formatOperation = (operation) => {
    const names = {
      'creative_chat': 'Creative Chat',
      'image_generation': 'Image Generation',
      'brand_analysis': 'Brand Analysis',
      'text_generation': 'Text Generation',
      'image_enhancement': 'Image Enhancement',
      'audience_research': 'Audience Research'
    };
    return names[operation] || operation.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Format date
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Operation', 'Credits Used', 'Cost (USD)', 'Details'];
    const rows = filteredHistory.map(item => [
      new Date(item.timestamp).toLocaleString(),
      formatOperation(item.operation),
      item.creditsUsed,
      creditsToUSD(item.creditsUsed),
      JSON.stringify(item.details || {})
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usage-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p>Loading usage history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Usage History</h1>
          <p className="page-subtitle">Track your credit usage and AI operations</p>
        </div>
        <button onClick={exportToCSV} className="btn btn-secondary">
          Export to CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '32px' }}>
        <div className="card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <h3 style={{ margin: 0, marginBottom: '8px', color: 'white', opacity: 0.9, fontSize: '0.9rem' }}>
            Total Credits Used
          </h3>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>
            {formatCredits(summary.totalCredits)}
          </div>
          <div style={{ fontSize: '0.9rem', opacity: 0.9, marginTop: '4px' }}>
            ≈ ${creditsToUSD(summary.totalCredits)} USD
          </div>
        </div>

        <div className="card">
          <h3 style={{ margin: 0, marginBottom: '8px', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
            Total Operations
          </h3>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-primary)' }}>
            {summary.totalOperations}
          </div>
          <div style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
            {dateFilter === 'all' ? 'All time' : dateFilter === 'today' ? 'Today' : dateFilter === 'week' ? 'This week' : 'This month'}
          </div>
        </div>

        <div className="card">
          <h3 style={{ margin: 0, marginBottom: '8px', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
            Average per Operation
          </h3>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-success)' }}>
            {summary.totalOperations > 0 ? formatCredits(Math.round(summary.totalCredits / summary.totalOperations)) : '0'}
          </div>
          <div style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
            ≈ ${summary.totalOperations > 0 ? creditsToUSD(Math.round(summary.totalCredits / summary.totalOperations)) : '0.00'} USD
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '4px', display: 'block' }}>
              Time Period
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                background: 'var(--color-bg-primary)',
                fontSize: '0.9rem'
              }}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>

          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '4px', display: 'block' }}>
              Operation Type
            </label>
            <select
              value={operationFilter}
              onChange={(e) => setOperationFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                background: 'var(--color-bg-primary)',
                fontSize: '0.9rem'
              }}
            >
              <option value="all">All Operations</option>
              {operationTypes.map(type => (
                <option key={type} value={type}>{formatOperation(type)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Usage History Table */}
      {filteredHistory.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📊</div>
          <h3>No usage history found</h3>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            {dateFilter !== 'all' || operationFilter !== 'all'
              ? 'Try adjusting your filters to see more results.'
              : 'Start using AI features to see your usage history here.'}
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.9rem'
          }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-secondary)', borderBottom: '2px solid var(--color-border)' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Date & Time</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Operation</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Tokens/Images</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Credits Used</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Cost (USD)</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.map((item, index) => (
                <tr key={item.id || index} style={{
                  borderBottom: '1px solid var(--color-border)',
                  transition: 'background 0.2s'
                }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontWeight: 500 }}>
                      {new Date(item.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                      {formatDate(item.timestamp)}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontWeight: 500 }}>{formatOperation(item.operation)}</div>
                    {item.details?.model && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                        {item.details.model}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {item.details?.tokens && (
                      <div>{item.details.tokens.toLocaleString()} tokens</div>
                    )}
                    {item.details?.images && (
                      <div>{item.details.images} image{item.details.images > 1 ? 's' : ''}</div>
                    )}
                    {!item.details?.tokens && !item.details?.images && (
                      <div style={{ color: 'var(--color-text-secondary)' }}>—</div>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>
                    {formatCredits(item.creditsUsed)}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', color: 'var(--color-text-secondary)' }}>
                    ${creditsToUSD(item.creditsUsed)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          table {
            font-size: 0.8rem;
          }
          th, td {
            padding: 8px 12px !important;
          }
        }
      `}</style>
    </div>
  );
}
